import { ContentCopy, DeployedCode } from '@project-lary/react-material-symbols';
import clsx from 'clsx';
import type { FetchETSTicketInformationResponseData } from 'gcs-common/clients/api/entities/tickets/tickets-api-helpers';
import { LEGACY_EVENTS } from 'gcs-common/config/googleAnalyticsLegacyConfig';
import { CHAT_ROUTES, ETS_ROUTES } from 'gcs-common/constants/routes';
import { useIsMobile } from 'gcs-common/design-system/hooks/use-media-query';
import { toast } from 'gcs-common/design-system/hooks/use-toast';
import { Button, ButtonSize, ButtonVariant } from 'gcs-common/design-system/ui/button';
import IconButton from 'gcs-common/design-system/ui/icon-button';
import useTrackOncePerSession from 'gcs-common/hooks/useTrackOncePerSession';
import { trackGALegacyEvent } from 'gcs-common/slices/googleAnalytics/googleAnalyticsThunks';
import { getIsMobile } from 'gcs-common/slices/init/initSelectors';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { getIsAgent } from '../../../slices/currentUser/currentUserSelectors';
import TicketMessageBubble from '../TicketMessageBubble/TicketMessageBuble';

interface TicketMessagePartProps {
  parts?: FetchETSTicketInformationResponseData['parts']['parts'];
  description?: string | null;
  manufacturer?: string | null;
  kbn?: string | null;
  amount?: number;
  articleNumber?: string | null;
  url?: string | null;
  isFromMe?: boolean | false;
  showOptions?: boolean | false;
  partType?: string | null;
  index?: number | null;
  className?: string | null;
  ticketId: string;
  externalJiraKey?: string | null;
  /** ONLINE PLUS shop of the ticket's house, e.g. laponeonlineplus.de */
  onlinePlusHost?: string | null;
}

const TicketMessagePart = (props: TicketMessagePartProps) => {
  const {
    description = null,
    manufacturer = null,
    kbn = null,
    isFromMe,
    showOptions,
    className,
    partType,
    index,
    externalJiraKey = null,
    onlinePlusHost = null,
    ticketId,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isMobileDevice = useSelector(getIsMobile);
  const buttonText = isMobile ? 'Zu ONLINE PLUS' : 'Zur ONLINE PLUS-Artikelseite';
  const isAgent = useSelector(getIsAgent);

  useTrackOncePerSession({
    event: LEGACY_EVENTS.ONLINE_PLUS_EXTERNAL_LINK_CLICKED(),
    eventKey: `op-external-link-clicked-${externalJiraKey}-${kbn}`,
    skip: !externalJiraKey || !kbn,
  });
  useTrackOncePerSession({
    event: LEGACY_EVENTS.MESSAGE_WITHOUT_OP_LINK_DISPLAYED(),
    eventKey: `message-without-op-link-displayed-${externalJiraKey}`,
    skip: !externalJiraKey,
  });

  const copyIdToClipboard = (ticketId: string) => {
    navigator.clipboard
      .writeText(ticketId)
      .then(() => {
        toast({
          description: 'Ticket-ID in Zwischenablage kopiert.',
          duration: 5000,
          variant: 'success',
        });
      })
      .catch(() => {});
  };

  // The ONLINE PLUS shop URL is constructed based on the house's onlinePlusHost.
  // In case of HFH, the OP host is per house not per app variant.
  // Falls back to GC if the backend doesn't send it.
  const url = encodeURI(
    `https://www.${onlinePlusHost || 'gconlineplus.de'}/?productId=${kbn}&ecid=md:app!src:beem!cn:ets!ct:op_button`,
  );

  const handleLinkClick = () => {
    dispatch(trackGALegacyEvent(LEGACY_EVENTS.ONLINE_PLUS_EXTERNAL_LINK_CLICKED()));
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const forwardingOption = {
    index,
    type: partType,
  };

  const canSharePart = !!ticketId && !!partType && (!!index || index === 0);

  const shareTicketMessagePart = () => {
    navigate(CHAT_ROUTES.TICKET_SHARE_MESSAGE(ticketId || '', partType || '', index || 0));
  };

  const handleToDetailsClick = () => {
    navigate(ETS_ROUTES.DASHBOARD_TICKET_DETAILS(ticketId));
  };

  return (
    <TicketMessageBubble
      isFromMe={isFromMe ?? false}
      showOptions={!kbn ? false : (showOptions ?? false)}
      className={`${className} max-w-[325px]`}
      forwardingOption={forwardingOption}
    >
      <div className="font-medium text-accent-9 text-sm">Ersatzteil-Team</div>
      <div className={`mt-2 flex items-center gap-2 rounded-xl px-4 py-3 ${kbn ? 'bg-success-4' : 'bg-warning-4'}`}>
        <DeployedCode className={`size-6 w-6 ${kbn ? 'fill-success-11' : 'fill-warning-11'}`} />
        <div className={`font-regular text-base ${kbn ? 'text-success-11' : 'text-warning-11'}`}>
          {kbn ? 'Artikel auf ONLINE PLUS gefunden' : 'Ungelisteter Artikel gefunden'}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <span className="text-neutral-9 text-sm">Bez.</span>
        <span className="text-black">{description}</span>

        <span className="text-neutral-9 text-sm">Hersteller</span>
        <span className="text-black">{manufacturer}</span>

        <span className="text-neutral-9 text-sm">KBN</span>
        <span className={clsx('text-black', { 'text-neutral-9': !kbn })}>{kbn || '(Nicht gelistet)'}</span>
      </div>

      <div className="mt-3 flex flex-col gap-4 pb-1">
        {!kbn && (
          <div className="text-neutral-11 text-sm">
            Der Artikel ist nicht gelistet. Dein Ansprechpartner wurde automatisch benachrichtigt. Du kannst den Artikel
            bei Bedarf zusätzlich weiterleiten.
          </div>
        )}
        {(!kbn || isAgent) && externalJiraKey && (
          <div className="flex flex-row items-center">
            <div className="mr-5 text-neutral-11 text-sm">Ticket-ID:</div>
            <div className="flex flex-1 flex-row items-center justify-center">
              <div className="mr-1 text-neutral-11 text-sm">{externalJiraKey}</div>
              <div>
                <IconButton
                  Icon={ContentCopy}
                  onClick={() => copyIdToClipboard(externalJiraKey)}
                  size="base"
                  iconSize="text-2xl"
                  variant="ghost_neutral"
                />
              </div>
            </div>
          </div>
        )}
        {kbn && (
          <Button
            type="button"
            variant={ButtonVariant.solid_accent}
            size={ButtonSize.xl}
            className="w-full"
            onClick={handleLinkClick}
          >
            {buttonText}
          </Button>
        )}
        {showOptions && canSharePart && (
          <Button
            type="button"
            variant={ButtonVariant.soft_neutral}
            size={ButtonSize.xl}
            className="w-full"
            onClick={shareTicketMessagePart}
          >
            Weiterleiten
          </Button>
        )}
        {isAgent && !isMobileDevice && (
          <Button
            type="button"
            variant={ButtonVariant.soft_neutral}
            size={ButtonSize.xl}
            className="w-full"
            onClick={handleToDetailsClick}
          >
            Details anzeigen
          </Button>
        )}
      </div>
    </TicketMessageBubble>
  );
};

export default TicketMessagePart;
